import { useRef, useEffect, useCallback, useState } from 'react';
import type { PixelData, Language } from '@vikings/types';
import {
  clearCanvas,
  renderPixels,
  Animator,
  getLogoPixels,
  getBattlefieldTile,
  getCloud1Pixels,
  getCloud2Pixels,
  getBirdFrames,
  getStandingDragonFrames,
} from '@vikings/pixel-engine';

import { t } from '@vikings/game-data';

interface EmptyBattlefieldCanvasProps {
  language?: Language;
  onDragonClick?: () => void;
}

interface CloudInstance {
  x: number;
  yRatio: number;
  speed: number;
  scale: number;
  type: 1 | 2;
}

interface BirdInstance {
  x: number;
  yRatio: number;
  speedX: number;
  scale: number;
  frame: number;
  timer: number;
  wobbleOffset: number;
}

/**
 * EmptyBattlefieldCanvas — displays an empty Nordic battlefield with a standing dragon.
 * Rendered when a player visits the game without authorization.
 */
export function EmptyBattlefieldCanvas({
  language = 'uk',
  onDragonClick,
}: EmptyBattlefieldCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const lastTimeRef = useRef<number>(0);
  const dragonBoundsRef = useRef<{ x: number; y: number; w: number; h: number } | null>(null);

  const [scale, setScale] = useState(3);
  const logoRef = useRef<PixelData>(getLogoPixels());
  const battlefieldRef = useRef<PixelData>(getBattlefieldTile());
  const cloud1Ref = useRef<PixelData>(getCloud1Pixels());
  const cloud2Ref = useRef<PixelData>(getCloud2Pixels());
  const birdFramesRef = useRef<PixelData[]>(getBirdFrames());

  // Dragon animator
  const dragonAnimRef = useRef<Animator | null>(null);

  // Dynamic sky objects: Clouds & Ravens
  const cloudsRef = useRef<CloudInstance[]>([
    { x: 30, yRatio: 0.16, speed: 12, scale: 2.5, type: 2 },
    { x: 220, yRatio: 0.12, speed: 8, scale: 2.0, type: 1 },
    { x: 360, yRatio: 0.22, speed: 15, scale: 3.0, type: 2 },
    { x: -70, yRatio: 0.28, speed: 10, scale: 2.2, type: 1 },
  ]);

  const birdsRef = useRef<BirdInstance[]>([
    { x: 60, yRatio: 0.19, speedX: 42, scale: 2, frame: 0, timer: 0, wobbleOffset: 0 },
    { x: -120, yRatio: 0.26, speedX: 50, scale: 3, frame: 1, timer: 0.05, wobbleOffset: 1.8 },
    { x: 250, yRatio: 0.14, speedX: 32, scale: 2, frame: 2, timer: 0.10, wobbleOffset: 3.5 },
  ]);

  // Initialize dragon animation
  useEffect(() => {
    const frames = getStandingDragonFrames();
    dragonAnimRef.current = new Animator({
      frames,
      fps: 3,
      loop: true,
      pingPong: false,
    });
  }, []);

  // Responsive resize
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = window.innerWidth;
      const h = window.innerHeight;

      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;

      const newScale = Math.max(2, Math.floor(Math.min(w, h) / 100));
      setScale(newScale);
    };

    resize();
    window.addEventListener('resize', resize);
    return () => window.removeEventListener('resize', resize);
  }, []);

  // Main render loop
  const render = useCallback(
    (timestamp: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.imageSmoothingEnabled = false;

      const delta = lastTimeRef.current
        ? Math.min(0.1, (timestamp - lastTimeRef.current) / 1000)
        : 0.016;
      lastTimeRef.current = timestamp;

      const dpr = window.devicePixelRatio || 1;
      const w = canvas.width / dpr;
      const h = canvas.height / dpr;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Update dragon animation
      dragonAnimRef.current?.update(delta);

      // Ground level
      const groundY = Math.round(h * 0.74);

      // 1. Nordic night/twilight sky
      clearCanvas(ctx, w, h, '#0c0f1d');

      const skyGrad = ctx.createLinearGradient(0, 0, 0, groundY);
      skyGrad.addColorStop(0, '#0c0f1d');
      skyGrad.addColorStop(0.5, '#171c35');
      skyGrad.addColorStop(1, '#253556');
      ctx.fillStyle = skyGrad;
      ctx.fillRect(0, 0, w, groundY);

      // Distant mountain silhouettes
      ctx.fillStyle = '#1c2842';
      ctx.beginPath();
      ctx.moveTo(0, groundY);
      for (let mx = 0; mx <= w; mx += 40) {
        const peakH = 15 + Math.sin(mx * 0.04) * 8 + Math.cos(mx * 0.015) * 6;
        ctx.lineTo(mx, groundY - peakH);
      }
      ctx.lineTo(w, groundY);
      ctx.closePath();
      ctx.fill();

      // 2. Clouds
      for (const cloud of cloudsRef.current) {
        cloud.x += cloud.speed * delta;
        const sprite = cloud.type === 1 ? cloud1Ref.current : cloud2Ref.current;
        const cloudW = sprite.width * cloud.scale;
        if (cloud.x > w + 40) {
          cloud.x = -cloudW - 30;
          cloud.yRatio = 0.1 + Math.random() * 0.22;
        }
        renderPixels(
          ctx,
          sprite,
          Math.round(cloud.x),
          Math.round(h * cloud.yRatio),
          Math.round(cloud.scale),
        );
      }

      // 3. Ravens
      for (const bird of birdsRef.current) {
        bird.x += bird.speedX * delta;
        bird.timer += delta;
        if (bird.timer >= 0.14) {
          bird.timer = 0;
          bird.frame = (bird.frame + 1) % 3;
        }
        if (bird.x > w + 50) {
          bird.x = -60;
          bird.yRatio = 0.12 + Math.random() * 0.2;
        }
        const birdY = h * bird.yRatio + Math.sin(timestamp * 0.0035 + bird.wobbleOffset) * 6;
        const birdFrame = birdFramesRef.current[bird.frame] ?? birdFramesRef.current[0]!;
        renderPixels(ctx, birdFrame, Math.round(bird.x), Math.round(birdY), bird.scale);
      }

      // 4. Battlefield ground tiles
      const tile = battlefieldRef.current;
      const tileScale = Math.max(2, Math.floor(scale * 0.75));
      const tileW = tile.width * tileScale;
      const tileH = tile.height * tileScale;

      for (let tx = 0; tx < w; tx += tileW) {
        for (let ty = groundY; ty < h; ty += tileH) {
          renderPixels(ctx, tile, tx, ty, tileScale);
        }
      }

      // Grass fringe highlight along horizon
      ctx.fillStyle = '#4a9a35';
      ctx.fillRect(0, groundY, w, 2);

      // 5. VIKINGS logo
      const logo = logoRef.current;
      const targetLogoW = Math.min(w * 0.84, 460);
      const logoScale = Math.max(4, Math.floor(targetLogoW / logo.width));
      const logoW = logo.width * logoScale;
      const logoX = Math.round((w - logoW) / 2);
      const logoY = Math.max(50, Math.round(h * 0.075));
      renderPixels(ctx, logo, logoX, logoY, logoScale);

      // 6. Standing 4-legged Dragon on the empty field (matching user reference sprite)
      const dragonFrame =
        dragonAnimRef.current?.getFrame() ?? getStandingDragonFrames()[0]!;
      const dragonScale = Math.min(
        7,
        Math.max(3, Math.floor(Math.min(w * 0.82, h * 0.44) / 48)),
      );
      // In the 56x48 sprite, the dragon's torso/head axis is at col 32 (tail extends to col 2 on the left)
      const dragonX = Math.round(w / 2 - dragonScale * 32);
      // Row 46 is the bottom claw row, row 47 is transparent padding
      const dragonY = groundY - Math.round(dragonScale * 46.5);

      // Atmospheric ground shadow beneath the dragon's 4 feet (spanning cols 21 to 49, centered at 35)
      ctx.fillStyle = 'rgba(8, 16, 12, 0.48)';
      ctx.beginPath();
      const shadowW = Math.round(dragonScale * 30);
      const shadowH = Math.round(dragonScale * 3.5);
      const shadowX = dragonX + Math.round(dragonScale * 35);
      const shadowY = groundY + Math.round(dragonScale * 1);
      ctx.ellipse(shadowX, shadowY, shadowW / 2, Math.max(3, shadowH / 2), 0, 0, Math.PI * 2);
      ctx.fill();

      // Render the Dragon
      renderPixels(ctx, dragonFrame, dragonX, dragonY, dragonScale);

      // Track dragon bounds for click/hover interaction
      dragonBoundsRef.current = {
        x: dragonX,
        y: dragonY,
        w: 56 * dragonScale,
        h: 48 * dragonScale,
      };

      animFrameRef.current = requestAnimationFrame(render);
    },
    [scale],
  );

  useEffect(() => {
    animFrameRef.current = requestAnimationFrame(render);
    return () => {
      if (animFrameRef.current) {
        cancelAnimationFrame(animFrameRef.current);
      }
    };
  }, [render]);

  const isPointInDragon = useCallback((clientX: number, clientY: number): boolean => {
    const canvas = canvasRef.current;
    if (!canvas) return false;
    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const b = dragonBoundsRef.current;
    if (!b) return false;
    const pad = 24; // Generous hit area
    return x >= b.x - pad && x <= b.x + b.w + pad && y >= b.y - pad && y <= b.y + b.h + pad;
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      if (isPointInDragon(e.clientX, e.clientY)) {
        onDragonClick?.();
      }
    },
    [isPointInDragon, onDragonClick],
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLCanvasElement>) => {
      if (isPointInDragon(e.clientX, e.clientY)) {
        onDragonClick?.();
      }
    },
    [isPointInDragon, onDragonClick],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      if (isPointInDragon(e.clientX, e.clientY)) {
        canvas.style.cursor = 'pointer';
      } else {
        canvas.style.cursor = 'default';
      }
    },
    [isPointInDragon],
  );

  return (
    <>
      <canvas
        ref={canvasRef}
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onMouseMove={handleMouseMove}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          imageRendering: 'pixelated',
          touchAction: 'manipulation',
        }}
      />
      {onDragonClick && (
        <div
          onClick={onDragonClick}
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(15, 23, 42, 0.90)',
            border: '2px solid #c9a654',
            color: '#ffe066',
            fontFamily: 'monospace',
            fontSize: '13px',
            fontWeight: 'bold',
            letterSpacing: '1.5px',
            padding: '10px 20px',
            cursor: 'pointer',
            boxShadow: '0 0 16px rgba(201, 166, 84, 0.45)',
            zIndex: 10,
            userSelect: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <span style={{ fontSize: '16px' }}>🐉</span>
          <span>{t(language, 'dragonHint')}</span>
        </div>
      )}
    </>
  );
}
